/**
 * Resolves `customConfig.heroImage` (SA-1681 fix-09) to the mobile/desktop
 * hero image URLs the FDS `GlobalNav`-style header renders behind — or to
 * `null` when there is no hero at all, in which case the header must render
 * SOLID from the start (a translucent header with no hero behind it would
 * be white text/logo on a white page — never allowed).
 *
 * Kept as its own small, unit-tested resolver rather than inline in
 * `Survey.tsx` so the four config shapes (absent / false / string / object)
 * have one place that owns the fallback rules. See `MeridianCustomConfig
 * .heroImage`'s own doc comment (`packages/shared/src/types/meridian-event
 * .ts`) and `docs/api/event-schema.md` for the full contract.
 */
import fordHero from '../assets/hero/ford-hero.jpg';
import lincolnHeroMobile from '../assets/hero/lincoln-hero-mobile.jpg';
import lincolnHeroDesktop from '../assets/hero/lincoln-hero-desktop.jpg';

export type HeroImageConfig = false | string | { mobile?: string; desktop?: string };

export interface ResolvedHeroImage {
  mobile: string;
  desktop: string;
}

/**
 * Brand default heroes. Ford uses one photo for both sizes (only the CSS
 * `background-position` differs, 50%/50% mobile vs 50%/45% desktop — see
 * `GlobalHeader.css`); Lincoln ships a distinct crop per size.
 */
const BRAND_DEFAULT_HERO: Record<'Ford' | 'Lincoln', ResolvedHeroImage> = {
  Ford: { mobile: fordHero, desktop: fordHero },
  Lincoln: { mobile: lincolnHeroMobile, desktop: lincolnHeroDesktop },
};

export interface ResolveHeroImageOptions {
  heroImage?: HeroImageConfig;
  brand: 'Ford' | 'Lincoln';
  /** Ford Pro's PNG logo has no white variant — same "no hero, solid header" rule as `heroImage: false`. */
  isFordProHeader?: boolean;
}

export function resolveHeroImage({
  heroImage,
  brand,
  isFordProHeader,
}: ResolveHeroImageOptions): ResolvedHeroImage | null {
  if (isFordProHeader) return null;
  if (heroImage === false) return null;

  const brandDefault = BRAND_DEFAULT_HERO[brand];

  if (heroImage === undefined) return brandDefault;

  if (typeof heroImage === 'string') {
    // Codex round-2: an empty or whitespace-only string is a blank config
    // value, not a real "no hero" opt-out (that's what the literal `false`
    // is for) — normalize it to the brand default rather than resolving to
    // two empty background-image URLs, which would render a translucent
    // header over nothing (unreadable regardless of the contrast backing).
    const trimmed = heroImage.trim();
    return trimmed ? { mobile: trimmed, desktop: trimmed } : brandDefault;
  }

  // Object form: a missing OR blank key falls back to the brand default for
  // that size only — same blank-normalization as the string case, applied
  // per field so a real desktop URL alongside a blank mobile one still
  // resolves to a usable image on both sizes.
  const mobile = heroImage.mobile?.trim();
  const desktop = heroImage.desktop?.trim();
  return {
    mobile: mobile || brandDefault.mobile,
    desktop: desktop || brandDefault.desktop,
  };
}
